import { inject, Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ROUTER_NAVIGATED, RouterNavigatedAction } from '@ngrx/router-store';
import { Action, select, Store } from '@ngrx/store';
import { setUser } from '@sentry/angular';
import { getNotificationsDropdownState, toggleNotifications } from 'app/app.actions';
import { IntercomData } from 'app/core/auth/user.model';
import { WebSocketService } from 'app/shared/backend';
import { sendResultToTask, TasksService } from 'app/shared/backend/tasks.service';
import { isWebSocketRefreshRequest, isWebSocketTaskResult, WebSocketTaskResult } from 'app/shared/backend/web-socket-message.model';
import { webSocketMessage } from 'app/shared/backend/web-socket.actions';
import { basemaps } from 'app/shared/ll/services/l-map.service';
import { LogEventService } from 'app/shared/log-event/log-event.directive';
import { ModalsService } from 'app/shared/ui/modals/modals.service';
import { loadScript } from 'app/shared/utils';
import { environment } from 'environments/environment';
import { versions } from 'environments/versions';
import { combineLatest, distinctUntilChanged, filter, fromEvent, map, mergeMap, pairwise, skipWhile, startWith, tap, withLatestFrom } from 'rxjs';
import { logout, refreshBranding, refreshCredentials, refreshUser } from './auth/auth.actions';
import { getUserInfo, getUserIntercom } from './auth/auth.reducer';
import { LogoutModalComponent } from './auth/logout.modal.component';
import { selectLayer, unselectLayer } from './map/layers/layers.actions';
import { LayersService } from './map/layers/layers.service';
import { changeBaseMap } from './map/map.actions';
import { loadNotifications } from './notifications/notifications.actions';
import { loadSavedReports } from './saved-reports/saved-reports.actions';


interface TriggerFn {
    (...args: unknown[]): void;
    c: (...args: unknown[]) => void;
    q: Array<unknown[]>;
}

const createTriggerFn = (): TriggerFn => {
    const i = (...args: unknown[]) => i.c(...args);
    i.q = [] as Array<unknown[]>;
    i.c = (...args: unknown[]) => i.q.push(args);
    return i;
};

declare global {
    interface Window {
        Intercom: TriggerFn;
        Upscope: TriggerFn;
        ga: TriggerFn;
        dataLayer: unknown[];
        hj: TriggerFn;
        _hjSettings: object;
        userflow: Record<string, TriggerFn>;
        _userflow: TriggerFn;
    }
}


const createIntercom = (apiKey: string, release: string) => {
    window.Intercom = window.Intercom || createTriggerFn();
    const payload = {
        app_id: apiKey,
        release: release,
    };
    window.Intercom('boot', payload);
    loadScript(`https://widget.intercom.io/widget/${apiKey}`);
};

const updateSentryUser = (user: IntercomData | null) => {
    setUser(user ? { email: user.email } : null);
};

const updateIntercomUser = (user: IntercomData | null) => {
    const payload = {
        name: user?.name,
        email: user?.email,
        user_id: user?.id,
        user_hash: user?.userHash,
        created_at: user?.registrationEpoch,
    };
    window.Intercom('update', payload);
};

const createUpscope = (apiKey: string) => {
    window.Upscope = window.Upscope || createTriggerFn();
    window.Upscope('init');
    loadScript(`https://code.upscope.io/${apiKey}.js`);
};

const updateUpscopeUser = (user: IntercomData | null) => {
    const payload = {
        uniqueId: user?.id,
        identities: [user?.name, user?.email],
    };
    window.Upscope('updateConnection', payload);
};

const createGoogleAnalytics = (apiKey: string, measurementId: string) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(['js', new Date()]);
    window.dataLayer.push(['config', measurementId]);
    loadScript(`https://www.googletagmanager.com/gtag/js?id=${measurementId}`);

    window.ga = window.ga || createTriggerFn();
    window.ga('create', apiKey, 'auto');
    loadScript(`https://www.google-analytics.com/analytics.js`);
};

const createGoogleTagManager = (apiKey: string) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js',
    });
    loadScript(`https://www.googletagmanager.com/gtm.js?id=${apiKey}`);
};

const createHotjar = (hjId: string, hjSv: string) => {
    window.hj = createTriggerFn();
    window._hjSettings = {
        hjid: hjId,
        hjsv: hjSv,
    };
    loadScript(`https://static.hotjar.com/c/hotjar-${hjId}.js?sv=${hjSv}`);
};

const createUserflow = (apiKey: string) => {
    window._userflow = createTriggerFn();
    const onload = () => {
        window._userflow.c = (...args: unknown[]) => {
            window.userflow[args[0] as string](...args.slice(1));
        };
        let args = window._userflow.q.shift();
        while (args) {
            window._userflow(...args);
            args = window._userflow.q.shift();
        }
    };
    window._userflow('init', apiKey);
    loadScript(`https://js.getuserflow.com/userflow.js`, { onload });
};

const updateUserflowUser = (userId: string | null) => {
    if (!userId) {
        window._userflow('reset');
    } else {
        window._userflow('identify', userId);
    }
};


const compareUrl = (first: string, second: string) => first === second;
const urlSelector = (action: RouterNavigatedAction) => action.payload.event.url;


@Injectable()
export class AppEffects {

    private store = inject(Store);

    private actions = inject(Actions);

    private layersService = inject(LayersService);

    private router = inject(Router);

    private taskService = inject(TasksService);

    private modalsService = inject(ModalsService);

    private ws = inject(WebSocketService);

    private logEventService = inject(LogEventService);

    private mql = window.matchMedia('(max-width: 1440px)');

    smallScreen = fromEvent(this.mql, 'change').pipe(map(() => this.mql.matches), startWith(this.mql.matches));

    oldRedirect = createEffect(() => this.router.events.pipe(
        tap(event => {
            if (event instanceof NavigationStart) {
                if (event.url.match(/^\/#/)) {
                    this.router.navigateByUrl(event.url.replace('/#', ''));
                }
            }
        })
    ), { dispatch: false });

    userChange = createEffect(() => this.store.pipe(
        skipWhile(() => !environment.production),
        select(getUserIntercom),
        tap(user => {
            updateSentryUser(user);
            updateIntercomUser(user);
            updateUpscopeUser(user);
        })
    ), { dispatch: false });

    showUpscopeOnBiggerScreen = createEffect(() =>
        combineLatest([this.store.pipe(select(getUserIntercom)), this.smallScreen]).pipe(
            skipWhile(() => !environment.production),
            tap(([user, smallScreen]) => {
                updateUserflowUser(user?.hasUserflow && !smallScreen ? user.id : null);
            })
        ),
        { dispatch: false }
    );

    disableIntercomOnMonitorAndSharedCma = createEffect(() => this.actions.pipe(
        skipWhile(() => !environment.production),
        ofType(ROUTER_NAVIGATED),
        distinctUntilChanged(compareUrl, urlSelector),
        tap(action => {
            const mainRoute = action.payload.routerState.root.firstChild;
            if (mainRoute) {
                window.Intercom('update', { hide_default_launcher: ['monitor', 'cma'].includes(mainRoute.url[0].path) });
            }
        })
    ), { dispatch: false });

    pageChange$ = createEffect(() => this.actions.pipe(
        skipWhile(() => !environment.production),
        ofType(ROUTER_NAVIGATED),
        distinctUntilChanged(compareUrl, urlSelector),
        tap(action => {
            window.ga('set', 'page', urlSelector(action));
            window.ga('send', 'pageview');
        })
    ), { dispatch: false });

    baseMapSelected$ = createEffect(() => this.actions.pipe(
        skipWhile(() => !environment.production),
        ofType(changeBaseMap),
        tap(action => {
            window.ga('send', {
                hitType: 'event',
                eventCategory: 'baseMap',
                eventAction: 'changed',
                eventLabel: basemaps.find(i => i.key === action.baseMap)?.title,
            });
        })
    ), { dispatch: false });

    layerSelected$ = createEffect(() => this.actions.pipe(
        skipWhile(() => !environment.production),
        ofType(selectLayer, unselectLayer),
        withLatestFrom(this.layersService.layersDef),
        tap(([action, layersDef]) => {
            window.ga('send', {
                hitType: 'event',
                eventCategory: 'layer',
                eventAction: action.type === selectLayer.type ? 'selected' : 'unselected',
                eventLabel: layersDef?.find(i => i.key === action.key)?.title,
            });
        })
    ), { dispatch: false });

    logLayerToggled$ = createEffect(() => this.actions.pipe(
        ofType(selectLayer, unselectLayer),
        tap(action => this.logEventService.logEvent({
            eventType: 'LAYER_TOGGLED',
            layerKey: action.key,
            enabled: action.type === selectLayer.type,
        }))
    ), { dispatch: false });

    toggleNotifications = createEffect(() => this.actions.pipe(
        ofType(toggleNotifications),
        withLatestFrom(this.store.select(getNotificationsDropdownState)),
        map(([_, state]) => {
            if (state) {
                this.router.navigate([{ outlets: { notifications: null } }]);
            } else {
                this.router.navigate([{ outlets: { notifications: 'list' } }]);
            }
        })
    ), { dispatch: false });

    webSocketMessageEffect = createEffect(() =>
        this.actions.pipe(
            ofType(webSocketMessage),
            filter(action => isWebSocketTaskResult(action.message)),
            tap(action => {
                const message = action.message as WebSocketTaskResult;
                // if we don't have task in `pending`, we keep message for later use
                const task = this.taskService.pending[message.taskId];
                if (typeof task === 'undefined') {
                    this.taskService.queue.push(message);
                    return;
                }
                // otherwise we can full-fill the task
                sendResultToTask(task, message);
            })
        ),
    { dispatch: false }
    );

    webSocketRefreshEffect = createEffect(() =>
        this.actions.pipe(
            ofType(webSocketMessage),
            map(action => action.message),
            filter(isWebSocketRefreshRequest),
            withLatestFrom(this.ws.connection),
            mergeMap(([message, { clientId }]) => {
                const actions: Action[] = [];
                if (message.clientId === clientId) {
                    return [];
                }
                if (message.payload.notifications) {
                    actions.push(loadNotifications());
                }
                if (message.payload.refreshUser) {
                    actions.push(refreshUser());
                }
                if (message.payload.savedReports) {
                    actions.push(loadSavedReports());
                }
                if (message.payload.credentials) {
                    actions.push(refreshCredentials());
                }
                if (message.payload.branding) {
                    actions.push(refreshBranding());
                }
                return actions;
            })
        )
    );

    loginEffect = createEffect(() =>
        this.store.select(getUserInfo).pipe(
            map(user => user.username),
            pairwise(),
            tap(([previousUsername, currentUsername]) => {
                if (previousUsername) {
                    this.ws.leaveRoom(`/${previousUsername}`);
                }
                if (currentUsername) {
                    this.ws.joinRoom(`/${currentUsername}`);
                }
            })
        ),
    { dispatch: false }
    );

    logoutEffect = createEffect(() =>
        this.actions.pipe(
            ofType(logout),
            tap(() => {
                this.modalsService.openModal(LogoutModalComponent);
            }),
        ),
    { dispatch: false }
    );

    constructor() {
        console.log('APP_VERSION: ', versions.revision);
        if (!environment.production) {
            return;
        }
        createIntercom(environment.intercomApiKey, versions.revision);
        createUpscope(environment.upscopeApiKey);
        createGoogleAnalytics(environment.gaApiKey, environment.gaMeasurementId);
        createGoogleTagManager(environment.gtmApiKey);
        createHotjar(environment.hotjarApiKey, environment.hotjarSv);
        createUserflow(environment.userflowApiKey);
    }
}
